import { useState, useEffect, useMemo } from 'react';
import {
  Grid3X3, Play, Pause, RefreshCw, AlertTriangle, TrendingUp, TrendingDown,
  CheckCircle, XCircle, Loader2, Info, ExternalLink, Activity, DollarSign,
  Lock, Infinity as InfinityIcon, Zap, Repeat,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useActivePolling } from '../hooks/useActivePolling';
import { InfoTip } from '../components/ui';
import TradingViewChart from '../components/TradingViewChart';
import DriftBanner from '../components/DriftBanner';
import {
  previewGrid, deployGridBot, listGridBots, getGridBot, getGridBotStats,
  stopGridBot, syncGridBot, setGridBotAutoRecover, getExchangeConnections, listGridTypes,
  getExchangeMarkets, getExchangeTicker,
} from '../services/api';

// Fallback shown for ~50ms before per-exchange markets load via CCXT.
const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT'];

// Sort: popular bases first, then by stablecoin quote priority, then alphabetic.
// Same pattern as Algo Orders so the most-traded pairs surface at the top of
// the full per-exchange list (Coinbase has ~900, Binance US ~300, etc.).
function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC', 'DOT', 'UNI', 'ATOM', 'NEAR', 'APT', 'ARB', 'OP'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    const baseRank  = POPULAR_BASES.indexOf(base);
    const quoteRank = POPULAR_QUOTES.indexOf(quote);
    return (quoteRank === -1 ? 100 : quoteRank) * 1000 + (baseRank === -1 ? 999 : baseRank);
  };
  return [...pairs].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

const VARIANT_ICONS = {
  spot: Grid3X3,
  futures: Zap,
  infinity: InfinityIcon,
  reverse: Repeat,
};
const VARIANT_ACCENTS = {
  spot:     { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40' },
  futures:  { color: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/40' },
  infinity: { color: 'text-violet-400',  bg: 'bg-violet-500/10',  border: 'border-violet-500/40' },
  reverse:  { color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/40' },
};

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function GridBotPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('create'); // 'create' | 'live'
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(true);

  const [config, setConfig] = useState({
    exchangeId: '',
    symbol: 'BTC/USDT',
    lowPrice: '',
    highPrice: '',
    gridCount: 20,
    totalAmount: 100,
    mode: 'arithmetic',
    name: '',
    gridType: 'spot',
    leverage: 5,
    marginMode: 'isolated',
    // Auto-recover (added 2026-06-08): when ON, the worker auto-redeploys
    // orders once activeOrders is empty + current price has returned to range.
    // Default ON for new bots — matches competitor behavior (3Commas, Pionex)
    // and matches user expectation. Toggle off for "fire once, stop on
    // depletion" workflows.
    autoRecover: true,
  });
  const [variantCatalog, setVariantCatalog] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);
  const [deploySuccess, setDeploySuccess] = useState(null);

  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(null);
  const [botDetails, setBotDetails] = useState(null);
  const [botStats, setBotStats] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load connected exchanges + variant catalog on mount
  useEffect(() => {
    (async () => {
      try {
        const [exRes, typesRes] = await Promise.all([
          getExchangeConnections().catch(() => null),
          listGridTypes().catch(() => null),
        ]);
        const conns = exRes?.data?.connections || exRes?.connections || exRes?.data?.exchanges || exRes?.exchanges || [];
        setConnectedExchanges(conns.filter(c => c.connected !== false));
        if (conns.length && !config.exchangeId) {
          setConfig(c => ({ ...c, exchangeId: conns[0].exchangeId }));
        }
        const types = typesRes?.data?.types || [];
        setVariantCatalog(types);
      } catch (err) {
        console.error('Failed to load grid initial data', err);
      } finally {
        setLoadingExchanges(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load bots whenever switching to live tab + 10s active polling
  const loadBots = async () => {
    try {
      const res = await listGridBots();
      setBots(res?.data || []);
    } catch (err) {
      console.error('Failed to load bots', err);
    }
  };
  useEffect(() => {
    if (activeTab === 'live') loadBots();
  }, [activeTab]);

  // Polling: re-fetch the bot list every 10s while on Live tab, AND if a bot
  // is selected, also refresh its details + stats so the right-hand panel
  // reflects state changes (e.g. status flipping from running → stopped after
  // a stop button click). Previously only the list refreshed and the detail
  // panel stayed stale until a hard refresh.
  const liveInterval = useActivePolling(10000, activeTab === 'live');
  useEffect(() => {
    if (!liveInterval) return;
    const tick = async () => {
      try {
        const res = await listGridBots();
        const list = res?.data || [];
        setBots(list);
        // If a bot is selected, refresh its reference + details/stats so the
        // detail panel reflects the latest status (running/stopped/failed).
        if (selectedBot) {
          const updated = list.find(b => b.botId === selectedBot.botId);
          if (updated) setSelectedBot(updated);
          try {
            const [d, s] = await Promise.all([
              getGridBot(selectedBot.botId).catch(() => null),
              getGridBotStats(selectedBot.botId).catch(() => null),
            ]);
            if (d) setBotDetails(d?.data);
            if (s) setBotStats(s?.data);
          } catch {}
        }
      } catch (err) {
        console.error('Live tab refresh failed', err);
      }
    };
    const id = setInterval(tick, liveInterval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveInterval, selectedBot?.botId]);

  // Load selected bot details + stats
  useEffect(() => {
    if (!selectedBot) { setBotDetails(null); setBotStats(null); return; }
    (async () => {
      try {
        const [d, s] = await Promise.all([
          getGridBot(selectedBot.botId),
          getGridBotStats(selectedBot.botId),
        ]);
        setBotDetails(d?.data);
        setBotStats(s?.data);
      } catch (err) {
        console.error('Failed to load bot details', err);
      }
    })();
  }, [selectedBot]);

  // Auto-preview when range/grid params change (debounced)
  useEffect(() => {
    setPreviewError(null);
    if (!config.lowPrice || !config.highPrice || !config.gridCount || !config.totalAmount) {
      setPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await previewGrid({
          lowPrice: parseFloat(config.lowPrice),
          highPrice: parseFloat(config.highPrice),
          gridCount: parseInt(config.gridCount, 10),
          totalAmount: parseFloat(config.totalAmount),
          mode: config.mode,
        });
        setPreview(res?.data);
      } catch (err) {
        setPreview(null);
        setPreviewError(err.message);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [config.lowPrice, config.highPrice, config.gridCount, config.totalAmount, config.mode]);

  async function handleDeploy() {
    setDeployError(null);
    setDeploySuccess(null);
    setDeploying(true);
    try {
      const isFutures = config.gridType === 'futures' || config.gridType === 'reverse';
      const payload = {
        ...config,
        lowPrice: parseFloat(config.lowPrice),
        highPrice: parseFloat(config.highPrice),
        gridCount: parseInt(config.gridCount, 10),
        totalAmount: parseFloat(config.totalAmount),
        leverage: isFutures ? Number(config.leverage) : undefined,
        marginMode: isFutures ? config.marginMode : undefined,
      };
      const res = await deployGridBot(payload);
      setDeploySuccess(res?.data?.bot);
      setActiveTab('live');
      await loadBots();
    } catch (err) {
      // Timeouts on grid deploy almost always mean "backend is still placing
      // orders, frontend gave up first" — the bot usually DID deploy. Refresh
      // the bot list so a successful row appears, and surface a calmer message
      // that tells the user to check there before retrying (avoids duplicate
      // deploys from panic-clicking).
      if (/timed? ?out/i.test(err?.message || '')) {
        try { await loadBots(); } catch {}
        setDeployError(
          'Deploy took longer than expected. Your bot may have still been created — check the Live Bots tab in a few seconds before retrying. If a row appears there, the deploy succeeded.'
        );
      } else {
        setDeployError(err.message);
      }
    } finally {
      setDeploying(false);
    }
  }

  async function handleStop(botId) {
    if (!confirm('Stop this grid bot? All open orders will be canceled.')) return;
    setBusy(true);
    try {
      await stopGridBot(botId);
      // Re-fetch list + replace the selectedBot reference so the detail panel
      // immediately reflects status='stopped' (Stop button hides, badge flips).
      // Previously the list updated but selectedBot stayed stale until polling
      // tick or hard refresh.
      const res = await listGridBots();
      const list = res?.data || [];
      setBots(list);
      if (selectedBot?.botId === botId) {
        const updated = list.find(b => b.botId === botId);
        if (updated) setSelectedBot(updated);
        const [d, s] = await Promise.all([
          getGridBot(botId).catch(() => null),
          getGridBotStats(botId).catch(() => null),
        ]);
        if (d) setBotDetails(d?.data);
        if (s) setBotStats(s?.data);
      }
    } catch (err) {
      alert('Stop failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleManualSync(botId) {
    setBusy(true);
    try {
      await syncGridBot(botId);
      const [d, s] = await Promise.all([getGridBot(botId), getGridBotStats(botId)]);
      setBotDetails(d?.data);
      setBotStats(s?.data);
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  }

  // Toggle auto-recover on an existing bot. Backend persists the flag on the
  // bot record; the worker reads it on the next tick to decide whether to
  // attempt auto-redeploy when conditions are met.
  async function handleToggleAutoRecover(botId, nextEnabled) {
    setBusy(true);
    try {
      await setGridBotAutoRecover(botId, nextEnabled);
      // Update local state immediately for snappy UX, then re-fetch to
      // confirm the server view.
      setBotDetails(prev => prev ? { ...prev, bot: { ...prev.bot, autoRecover: nextEnabled } } : prev);
      const [d, list] = await Promise.all([getGridBot(botId), listGridBots()]);
      setBotDetails(d?.data);
      const fresh = (list?.data || []).find(b => b.botId === botId);
      if (fresh) setSelectedBot(fresh);
      setBots(list?.data || []);
    } catch (err) {
      alert('Could not change auto-recover setting: ' + err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <Grid3X3 className="w-7 h-7 text-emerald-400" />
          <h1 className="text-2xl font-bold text-white">Grid Bot</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Starter+</span>
        </div>
        <p className="text-sm text-gray-400">Place N buy/sell orders across a price range. Profits from oscillation — every grid crossed locks in a small profit. <span className="text-emerald-400">Real CCXT execution. No simulation.</span></p>
      </header>

      <div className="border-b border-dark-700/50 flex gap-1">
        <button onClick={() => setActiveTab('create')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'create' ? 'border-emerald-400 text-white' : 'border-transparent text-gray-500 hover:text-white'}`}>
          Create Grid Bot
        </button>
        <button onClick={() => setActiveTab('live')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'live' ? 'border-emerald-400 text-white' : 'border-transparent text-gray-500 hover:text-white'}`}>
          Live Bots {bots.length > 0 && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-dark-700 text-gray-400">{bots.length}</span>}
        </button>
      </div>

      {activeTab === 'create' && (
        <>
          <VariantSelector
            catalog={variantCatalog}
            selected={config.gridType}
            onSelect={(id) => setConfig(c => ({ ...c, gridType: id }))}
          />
          <TradingViewChart symbol={config.symbol} exchangeId={config.exchangeId} interval="60" height={420} />
          <CreateGrid
          config={config}
          setConfig={setConfig}
          connectedExchanges={connectedExchanges}
          loadingExchanges={loadingExchanges}
          preview={preview}
          previewError={previewError}
          deploying={deploying}
          deployError={deployError}
          deploySuccess={deploySuccess}
          onDeploy={handleDeploy}
        />
        </>
      )}

      {activeTab === 'live' && (
        <LiveBots
          bots={bots}
          selectedBot={selectedBot}
          setSelectedBot={setSelectedBot}
          botDetails={botDetails}
          botStats={botStats}
          busy={busy}
          onStop={handleStop}
          onSync={handleManualSync}
          onToggleAutoRecover={handleToggleAutoRecover}
        />
      )}
    </div>
  );
}

// ─── Variant Selector ────────────────────────────────────────────────────────

function VariantSelector({ catalog, selected, onSelect }) {
  if (!catalog || catalog.length === 0) {
    return null;
  }
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5">
      <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
        <Grid3X3 className="w-4 h-4 text-emerald-400" /> Grid Variant
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {catalog.map(v => {
          const Icon = VARIANT_ICONS[v.id] || Grid3X3;
          const accent = VARIANT_ACCENTS[v.id] || VARIANT_ACCENTS.spot;
          const isSelected = selected === v.id;
          const locked = !v.unlocked;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => !locked && onSelect(v.id)}
              disabled={locked}
              className={`text-left rounded-xl p-3 border transition-all ${
                isSelected
                  ? `${accent.bg} ${accent.border} ${accent.color}`
                  : locked
                  ? 'bg-dark-700/30 border-dark-700/40 text-gray-600 cursor-not-allowed opacity-60'
                  : 'bg-dark-700/30 border-dark-600/40 text-gray-300 hover:border-dark-500'
              }`}>
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`w-4 h-4 ${isSelected ? accent.color : ''}`} />
                <span className={`text-sm font-bold ${isSelected ? accent.color : ''}`}>{v.label}</span>
                {locked && <Lock className="w-3 h-3 ml-auto text-gray-600" />}
                {v.needsLeverage && !locked && <Zap className="w-3 h-3 ml-auto text-blue-400" title="Uses leverage" />}
              </div>
              <p className="text-[11px] leading-relaxed text-gray-500">{v.description}</p>
              {locked && (
                <p className="text-[10px] mt-1.5 text-amber-400">Requires {v.requiredTier?.toUpperCase()}+ tier</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Create tab ──────────────────────────────────────────────────────────────

function CreateGrid({ config, setConfig, connectedExchanges, loadingExchanges, preview, previewError, deploying, deployError, deploySuccess, onDeploy }) {
  const set = (k) => (e) => setConfig(c => ({ ...c, [k]: e.target.value }));
  const isFutures = config.gridType === 'futures' || config.gridType === 'reverse';

  // Dynamic per-exchange pair list, fetched via CCXT loadMarkets.
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');

  // Live ticker — same 15s refresh as Algo Orders / CEX Trading. Powers the
  // "Use market" buttons and percent-from-spot quick chips on the range fields.
  const [tickerPrice, setTickerPrice] = useState(null);
  useEffect(() => {
    if (!config.exchangeId || !config.symbol) return;
    let cancelled = false;
    setTickerPrice(null);
    async function refresh() {
      try {
        const t = await getExchangeTicker(config.exchangeId, config.symbol);
        if (!cancelled && t?.last && t.last > 0) setTickerPrice(t.last);
      } catch {}
    }
    refresh();
    const id = setInterval(refresh, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [config.exchangeId, config.symbol]);

  // Quote-asset formatter for hint text on the chips
  const quote = (config.symbol || '/').split('/')[1] || '';
  const fmt = (n) => {
    if (!Number.isFinite(n)) return '—';
    if (['USDT', 'USDC', 'USD', 'BUSD', 'DAI'].includes(quote)) return n.toFixed(2);
    return n.toFixed(4);
  };

  useEffect(() => {
    if (!config.exchangeId) return;
    let cancelled = false;
    setPairsLoading(true);
    setPairSearch('');
    getExchangeMarkets(config.exchangeId)
      .then(res => {
        if (cancelled) return;
        const all = (res?.markets || []).map(m => m.symbol);
        if (all.length === 0) { setPairs(FALLBACK_PAIRS); return; }
        const sorted = sortPairs(all);
        setPairs(sorted);
        if (!sorted.includes(config.symbol)) {
          setConfig(c => ({ ...c, symbol: sorted[0] }));
        }
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.exchangeId]);

  const filteredPairs = pairSearch
    ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase())).slice(0, 100)
    : pairs.slice(0, 100);

  const canDeploy =
    config.exchangeId && config.symbol &&
    parseFloat(config.lowPrice) > 0 && parseFloat(config.highPrice) > parseFloat(config.lowPrice) &&
    parseInt(config.gridCount, 10) >= 2 && parseFloat(config.totalAmount) > 0 &&
    (!isFutures || (Number(config.leverage) >= 1 && Number(config.leverage) <= 125)) &&
    !deploying;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: Config */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Exchange</label>
          {loadingExchanges ? (
            <div className="bg-dark-700/50 rounded-lg p-3 text-gray-500 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading exchanges…
            </div>
          ) : connectedExchanges.length === 0 ? (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-amber-400 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">No exchange connected.</p>
                <p className="text-xs text-amber-300 mt-0.5">
                  Go to <a href="/settings?tab=connections" className="underline">Settings → Connections</a> to add Binance, Coinbase, or any of 13 other exchanges.
                </p>
              </div>
            </div>
          ) : (
            <select value={config.exchangeId} onChange={set('exchangeId')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm">
              {connectedExchanges.map(c => (
                <option key={c.exchangeId} value={c.exchangeId}>{c.name || c.exchangeId}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-gray-400">Pair</label>
            <span className="text-[10px] text-gray-500">
              {pairsLoading ? 'Loading…' : `${pairs.length.toLocaleString()} on this exchange`}
            </span>
          </div>
          <input
            type="text"
            value={pairSearch}
            onChange={(e) => setPairSearch(e.target.value)}
            placeholder="Search pair (e.g. BTC, ETH/USDC)"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono placeholder-gray-600 mb-2"
          />
          <div className="max-h-40 overflow-y-auto bg-dark-700/50 border border-dark-600 rounded-lg">
            {filteredPairs.length === 0 ? (
              <div className="p-3 text-xs text-gray-500">No pairs match "{pairSearch}"</div>
            ) : (
              filteredPairs.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setConfig(c => ({ ...c, symbol: p }))}
                  className={`block w-full text-left px-3 py-1.5 text-sm font-mono transition-colors ${
                    config.symbol === p
                      ? 'bg-accent-primary/15 text-accent-primary'
                      : 'text-gray-300 hover:bg-dark-600'
                  }`}
                >
                  {p}
                </button>
              ))
            )}
          </div>
          {pairs.length > 100 && !pairSearch && (
            <p className="text-[10px] text-gray-500 mt-1">Showing first 100 — refine search to find more</p>
          )}
          <div className="mt-2 text-xs text-gray-500">
            Selected: <span className="font-mono text-white">{config.symbol || '(none)'}</span>
          </div>
        </div>

        {/* Range — Low + High with one-click % chips so users don't need a calculator */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-gray-400">Range</label>
            {tickerPrice ? (
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-gray-500">Market: <span className="text-cyan-400 font-mono">{fmt(tickerPrice)} {quote}</span></span>
                <button type="button"
                  onClick={() => setConfig(c => ({ ...c, lowPrice: (tickerPrice * 0.97).toFixed(2), highPrice: (tickerPrice * 1.03).toFixed(2) }))}
                  className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25">
                  ±3% around market
                </button>
              </div>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] text-red-400 uppercase tracking-wide flex items-center gap-1.5">
                  Low (buys below)
                  <InfoTip side="top">The bottom of your range. Buy orders sit between here and current price. Pick from CHART support — a level price has actually bounced at 2-3 times in the last 1-4 weeks. NOT a round number or arbitrary percentage from spot.</InfoTip>
                </label>
                {tickerPrice && (
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 5, 10].map(p => (
                      <button key={p} type="button"
                        onClick={() => setConfig(c => ({ ...c, lowPrice: (tickerPrice * (1 - p / 100)).toFixed(2) }))}
                        className="px-1.5 py-0.5 text-[10px] rounded bg-dark-900 border border-dark-700 text-gray-400 hover:border-red-500/40 hover:text-red-300"
                        title={`Spot − ${p}% = ${fmt(tickerPrice * (1 - p / 100))} ${quote}`}>
                        -{p}%
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input type="number" step="any" value={config.lowPrice} onChange={set('lowPrice')} placeholder={tickerPrice ? fmt(tickerPrice * 0.97) : 'Low price'}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
              {tickerPrice && config.lowPrice && Number(config.lowPrice) > 0 && (
                <p className="text-[10px] text-gray-500 mt-1 font-mono">
                  → {((Number(config.lowPrice) / tickerPrice - 1) * 100).toFixed(2)}% from spot
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] text-emerald-400 uppercase tracking-wide flex items-center gap-1.5">
                  High (sells above)
                  <InfoTip side="top">The top of your range. Sell orders sit between current price and here. Pick from CHART resistance — a level price has been rejected at 2-3 times recently. Range too narrow = price walks out fast. Too wide = grids never fire.</InfoTip>
                </label>
                {tickerPrice && (
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 5, 10].map(p => (
                      <button key={p} type="button"
                        onClick={() => setConfig(c => ({ ...c, highPrice: (tickerPrice * (1 + p / 100)).toFixed(2) }))}
                        className="px-1.5 py-0.5 text-[10px] rounded bg-dark-900 border border-dark-700 text-gray-400 hover:border-emerald-500/40 hover:text-emerald-300"
                        title={`Spot + ${p}% = ${fmt(tickerPrice * (1 + p / 100))} ${quote}`}>
                        +{p}%
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input type="number" step="any" value={config.highPrice} onChange={set('highPrice')} placeholder={tickerPrice ? fmt(tickerPrice * 1.03) : 'High price'}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
              {tickerPrice && config.highPrice && Number(config.highPrice) > 0 && (
                <p className="text-[10px] text-gray-500 mt-1 font-mono">
                  → +{((Number(config.highPrice) / tickerPrice - 1) * 100).toFixed(2)}% from spot
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
              Grid Count ({config.gridCount} grids)
              <InfoTip side="top">How many lines to spread across the range. 15-30 is sweet spot. Critical: profit per crossing must exceed 2× the exchange's round-trip fee. Too few grids = profitable per crossing but rare. Too many = grids fire often but each crossing loses to fees.</InfoTip>
            </label>
            <input type="range" min="2" max="100" value={config.gridCount} onChange={set('gridCount')} className="w-full" />
            <p className="text-[10px] text-gray-500 mt-1">More grids = smaller profit per grid but more frequent fills</p>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
              Total Amount (USD)
              <InfoTip side="top">Capital allocated to the bot. Auto-split half base (BTC/ETH/etc.), half quote (USDC/USDT). Start with $100-500 for first grid. Don't deploy full bankroll — verify the range works for at least a week first.</InfoTip>
            </label>
            <input type="number" step="any" value={config.totalAmount} onChange={set('totalAmount')} placeholder="100"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
            Mode
            <InfoTip side="right">ARITHMETIC: grids equally spaced in absolute $. GEOMETRIC: equally spaced in %. Geometric matches crypto's multiplicative behavior — better default for volatile pairs. Use Arithmetic for stablecoin-quoted pairs in narrow ranges.</InfoTip>
          </label>
          <div className="flex gap-2">
            <button onClick={() => setConfig(c => ({ ...c, mode: 'arithmetic' }))}
              className={`flex-1 p-2 rounded-lg text-xs font-medium border ${config.mode === 'arithmetic' ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>
              Arithmetic (equal $ gaps)
            </button>
            <button onClick={() => setConfig(c => ({ ...c, mode: 'geometric' }))}
              className={`flex-1 p-2 rounded-lg text-xs font-medium border ${config.mode === 'geometric' ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>
              Geometric (equal % gaps)
            </button>
          </div>
        </div>

        {isFutures && (
          <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-3 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-blue-400">
              <Zap className="w-3.5 h-3.5" /> Futures settings
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                  Leverage ({config.leverage}x)
                  <InfoTip side="top">Use 2-3× MAX for grids. Higher leverage liquidates on small adverse moves — and grid bots EXPECT adverse moves through the range. 10×+ on grids is a common way new users blow up accounts.</InfoTip>
                </label>
                <input type="range" min="1" max="125" value={config.leverage}
                  onChange={e => setConfig(c => ({ ...c, leverage: Number(e.target.value) }))}
                  className="w-full" />
                <p className="text-[10px] text-gray-500 mt-1">Higher leverage = larger position per dollar but liquidation risk grows.</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                  Margin Mode
                  <InfoTip side="top">ISOLATED (recommended) caps loss to position margin. CROSS uses your entire account — ONE bad grid can wipe ALL your futures positions. Always Isolated for grids unless you specifically know why you want Cross.</InfoTip>
                </label>
                <div className="flex gap-2">
                  <button onClick={() => setConfig(c => ({ ...c, marginMode: 'isolated' }))}
                    className={`flex-1 p-2 rounded-lg text-xs font-medium border ${config.marginMode === 'isolated' ? 'bg-blue-500/10 border-blue-500/40 text-blue-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>
                    Isolated
                  </button>
                  <button onClick={() => setConfig(c => ({ ...c, marginMode: 'cross' }))}
                    className={`flex-1 p-2 rounded-lg text-xs font-medium border ${config.marginMode === 'cross' ? 'bg-blue-500/10 border-blue-500/40 text-blue-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>
                    Cross
                  </button>
                </div>
              </div>
            </div>
            {config.gridType === 'reverse' && (
              <p className="text-[11px] text-amber-300/80 leading-relaxed">
                <strong>Reverse Grid</strong> opens shorts above current price and covers below.
                Profits when the market trades sideways or down within your range. Liquidation risk is real — start with low leverage.
              </p>
            )}
            {config.gridType === 'infinity' && (
              <p className="text-[11px] text-violet-300/80 leading-relaxed">Infinity grids ignore the upper bound — auto-extend forever as price rises.</p>
            )}
          </div>
        )}

        {config.gridType === 'infinity' && !isFutures && (
          <div className="bg-violet-500/5 border border-violet-500/15 rounded-xl p-3 text-[11px] text-violet-300/80 leading-relaxed">
            <strong>Infinity Grid</strong> — the upper-bound price you set is just the starting top.
            When the highest sell fills, the grid auto-extends one step upward and keeps booking profit.
            The bot never "tops out". Pair with a lower bound that represents your max DCA depth.
          </div>
        )}

        {/* Auto-recover toggle — shipped 2026-06-08 in response to user feedback
            that the bot looked "stuck" when price exited the range then came
            back. With autoRecover ON, the worker redeploys orders automatically
            once price returns to range. Anti-thrash gate on backend prevents
            redeploying more than once per hour. */}
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!config.autoRecover}
              onChange={(e) => setConfig(c => ({ ...c, autoRecover: e.target.checked }))}
              className="mt-0.5 accent-emerald-500"
            />
            <div className="flex-1">
              <p className="text-white text-sm font-semibold">Auto-recover when price re-enters range</p>
              <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                If the grid runs out of active orders (price moved outside the range and came back, or all levels filled in a strong trend), the bot redeploys a fresh set of orders automatically. Recommended — matches the behavior of established grid platforms.
              </p>
              <p className="text-gray-500 text-[10px] mt-1.5">
                Cooldown: at most once per hour. Turn off if you want the bot to stop earning when depleted instead.
              </p>
            </div>
          </label>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Bot Name (optional)</label>
          <input value={config.name} onChange={set('name')} placeholder={`Grid ${config.symbol}`}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm" />
        </div>

        {deployError && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{deployError}</span>
          </div>
        )}
        {deploySuccess && (
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 text-emerald-400 text-sm flex items-start gap-2">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Deployed: {deploySuccess.name}. Check the Live Bots tab.</span>
          </div>
        )}

        <button onClick={onDeploy} disabled={!canDeploy}
          className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:bg-dark-600 disabled:text-gray-500 text-black font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
          {deploying ? <><Loader2 className="w-4 h-4 animate-spin" /> Deploying…</> : <><Play className="w-4 h-4" /> Deploy Grid Bot (real orders)</>}
        </button>
      </div>

      {/* Right: Preview */}
      <div className="space-y-4">
        <PreviewCard preview={preview} previewError={previewError} config={config} />
        <RiskCallout />
      </div>
    </div>
  );
}

function PreviewCard({ preview, previewError, config }) {
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5">
      <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
        <Activity className="w-4 h-4 text-emerald-400" /> Preview
      </h3>
      {previewError ? (
        <p className="text-red-400 text-sm">{previewError}</p>
      ) : !preview ? (
        <p className="text-gray-500 text-sm">Enter range + grid count to see live preview.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Profit per grid" value={`${formatNum(preview.profitPerGridPct, 3)}%`} accent="emerald" />
            <Stat label="Grids" value={preview.gridCount - 1} />
            <Stat label="Amount per grid" value={`$${formatNum(preview.amountPerGrid, 2)}`} />
            <Stat label="Est. annualized*" value={`${formatNum(preview.estimatedAnnualizedReturnPct, 0)}%`} accent="emerald" />
          </div>
          <p className="text-[10px] text-gray-600">
            *Assumes ~5 round-trips/day (typical for medium-volatility pairs in a sideways market). Actual returns vary with price action — outside the range, the bot stops earning.
          </p>

          {/* Grid lines visualization */}
          <div className="relative bg-dark-900/60 border border-dark-700/50 rounded-lg p-3 h-48 overflow-hidden">
            <p className="text-[10px] text-gray-500 mb-2">Grid levels (top = high, bottom = low)</p>
            <div className="absolute inset-x-3 top-8 bottom-3 flex flex-col-reverse">
              {preview.levels.slice(0, 50).map((level, i) => (
                <div key={i} className="flex-1 border-t border-dashed border-dark-600/40 flex items-center px-2">
                  <span className="text-[9px] text-gray-500 font-mono">${formatNum(level, level >= 100 ? 2 : 6)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, subtext }) {
  const accentClass = accent === 'emerald' ? 'text-emerald-400' : accent === 'red' ? 'text-red-400' : 'text-white';
  return (
    <div className="bg-dark-700/30 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-bold font-mono mt-0.5 ${accentClass}`}>{value}</p>
      {subtext && <p className="text-[9px] text-gray-500 mt-0.5 font-mono">{subtext}</p>}
    </div>
  );
}

function RiskCallout() {
  return (
    <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5">
      <h3 className="text-sm font-bold text-amber-400 mb-2 flex items-center gap-2">
        <Info className="w-4 h-4" /> What grid bots are good for
      </h3>
      <ul className="space-y-2 text-xs text-gray-300">
        <li>✓ <span className="text-white font-medium">Sideways markets</span> — earns from chop within your range.</li>
        <li>✓ <span className="text-white font-medium">Volatile but range-bound</span> assets — more grids fill per day.</li>
        <li>✗ <span className="text-white font-medium">Strong trends</span> — if price breaks the range and stays out, the bot accumulates one side and stops earning.</li>
        <li>✗ <span className="text-white font-medium">Memecoins with rug risk</span> — if liquidity disappears your unfilled orders strand on the order book.</li>
      </ul>
      <p className="text-[10px] text-gray-500 mt-3">
        Tip: pick a range based on the asset's last 7-day high and low ± 5%. Use 20-50 grids for blue chips, 5-15 for memecoins.
      </p>
    </div>
  );
}

// ─── Live tab ────────────────────────────────────────────────────────────────

function LiveBots({ bots, selectedBot, setSelectedBot, botDetails, botStats, busy, onStop, onSync, onToggleAutoRecover }) {
  // Same Active / Stopped / All filter pattern as Algo Orders so the list
  // stays scannable as users accumulate historical bots.
  const [statusFilter, setStatusFilter] = useState('active');

  const counts = useMemo(() => ({
    active: bots.filter(b => b.status === 'running').length,
    stopped: bots.filter(b => b.status === 'stopped' || b.status === 'failed' || b.status === 'completed').length,
    all: bots.length,
  }), [bots]);

  const filteredBots = useMemo(() => {
    if (statusFilter === 'active') return bots.filter(b => b.status === 'running');
    if (statusFilter === 'stopped') return bots.filter(b => b.status !== 'running');
    return bots;
  }, [bots, statusFilter]);

  if (bots.length === 0) {
    return (
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
        <Grid3X3 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
        <p className="text-white font-medium mb-1">No grid bots yet</p>
        <p className="text-sm text-gray-500">Switch to <span className="text-emerald-400">Create Grid Bot</span> to deploy your first one.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Bot list */}
      <div className="lg:col-span-1 space-y-2">
        {/* Filter tabs */}
        <div className="inline-flex bg-dark-800 rounded-lg p-0.5 border border-dark-700/50 mb-2 text-xs">
          {[
            { id: 'active',  label: 'Active',  count: counts.active },
            { id: 'stopped', label: 'Stopped', count: counts.stopped },
            { id: 'all',     label: 'All',     count: counts.all },
          ].map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setStatusFilter(t.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                statusFilter === t.id
                  ? 'bg-emerald-500/15 text-emerald-300'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {t.label}
              <span className={`text-[10px] font-mono px-1 py-0.5 rounded ${statusFilter === t.id ? 'bg-emerald-500/20' : 'bg-dark-700'}`}>{t.count}</span>
            </button>
          ))}
        </div>
        {filteredBots.length === 0 ? (
          <div className="bg-dark-800/50 rounded-xl border border-dark-700/50 p-6 text-center">
            <p className="text-sm text-gray-500">No {statusFilter === 'active' ? 'active' : statusFilter === 'stopped' ? 'stopped' : ''} bots.</p>
          </div>
        ) : filteredBots.map(bot => {
          const active = selectedBot?.botId === bot.botId;
          const variantId = bot.gridType || 'spot';
          const VariantIcon = VARIANT_ICONS[variantId] || Grid3X3;
          const accent = VARIANT_ACCENTS[variantId] || VARIANT_ACCENTS.spot;
          return (
            <button key={bot.botId} onClick={() => setSelectedBot(bot)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${active ? 'bg-emerald-500/10 border-emerald-400/40' : 'bg-dark-800/50 border-dark-700/50 hover:border-dark-600'}`}>
              <div className="flex items-center justify-between mb-1 gap-2">
                <span className="text-sm font-bold text-white truncate">{bot.name}</span>
                <StatusBadge status={bot.status} />
              </div>
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${accent.bg} ${accent.color} uppercase`}>
                  <VariantIcon className="w-2.5 h-2.5" />
                  {variantId}
                </span>
                {bot.leverage && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400">
                    {bot.leverage}x · {bot.marginMode || 'isolated'}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 font-mono">{bot.exchangeId} · {bot.symbol}</p>
              <p className="text-[10px] text-gray-600 mt-1">${formatNum(bot.lowPrice, 2)} — ${formatNum(bot.highPrice, 2)} · {bot.gridCount} grids</p>
            </button>
          );
        })}
      </div>

      {/* Bot details */}
      <div className="lg:col-span-2">
        {selectedBot ? (
          <BotDetails bot={selectedBot} details={botDetails} stats={botStats} busy={busy} onStop={onStop} onSync={onSync} onToggleAutoRecover={onToggleAutoRecover} />
        ) : (
          <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
            <p className="text-sm text-gray-500">Select a bot to see details, fills, and active orders.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    running: { label: 'Running', class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    stopped: { label: 'Stopped', class: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
    error: { label: 'Error', class: 'bg-red-500/15 text-red-400 border-red-500/30' },
  };
  const s = map[status] || { label: status, class: 'bg-dark-700 text-gray-400' };
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.class}`}>{s.label}</span>;
}

function BotDetails({ bot, details, stats, busy, onStop, onSync, onToggleAutoRecover }) {
  const activeOrders = details?.state?.activeOrders?.filter(o => o.status === 'open') || [];
  const fills = details?.recentFills || [];
  // Auto-recover lives on the bot record. `details.bot` is the freshly-fetched
  // server view; fall back to the row data from the list if details haven't
  // loaded yet. Existing pre-2026-06-08 bots have `autoRecover: undefined`
  // which displays + behaves like OFF.
  const autoRecover = !!(details?.bot?.autoRecover ?? bot.autoRecover);
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-white flex items-center gap-2 flex-wrap">
            {bot.name}
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${(VARIANT_ACCENTS[bot.gridType || 'spot']).bg} ${(VARIANT_ACCENTS[bot.gridType || 'spot']).color} uppercase`}>
              {bot.gridType || 'spot'}{bot.leverage ? ` ${bot.leverage}x` : ''}
            </span>
            {autoRecover && bot.status === 'running' && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 uppercase tracking-wider" title="Auto-recover is ON — bot will redeploy itself when activeOrders is empty and price is back in range">
                Auto-recover
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500 font-mono mt-0.5">{bot.exchangeId} · {bot.symbol} · {bot.gridCount} grids · ${formatNum(bot.lowPrice, 2)} — ${formatNum(bot.highPrice, 2)}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onSync(bot.botId)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-dark-600 text-gray-300 hover:border-dark-500 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Sync
          </button>
          {bot.status === 'running' && (
            <button onClick={() => onStop(bot.botId)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 disabled:opacity-50">
              <Pause className="w-3 h-3" /> Stop
            </button>
          )}
        </div>
      </div>

      {/* Auto-recover toggle — surfaced inline for existing bots so users can
          opt in without redeploying. Hidden for stopped/failed bots since
          there's nothing to auto-recover. */}
      {bot.status === 'running' && onToggleAutoRecover && (
        <div className={`rounded-lg p-3 border ${autoRecover ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRecover}
              disabled={busy}
              onChange={(e) => onToggleAutoRecover(bot.botId, e.target.checked)}
              className="mt-0.5 accent-emerald-500"
            />
            <div className="flex-1">
              <p className="text-white text-xs font-semibold">
                Auto-recover when price re-enters range
                {autoRecover ? (
                  <span className="ml-2 text-emerald-400 text-[10px] uppercase tracking-wider">ON</span>
                ) : (
                  <span className="ml-2 text-gray-500 text-[10px] uppercase tracking-wider">OFF</span>
                )}
              </p>
              <p className="text-gray-500 text-[11px] mt-0.5 leading-relaxed">
                {autoRecover
                  ? 'Worker will redeploy orders within ~2 min of price returning to range. Rate-limited to once per hour.'
                  : 'Bot will stop earning once activeOrders is empty until you manually redeploy. Toggle on to make it self-healing.'}
              </p>
            </div>
          </label>
        </div>
      )}

      {stats && (
        <>
          {/* Position-integrity drift banner — verifies the platform's tracked
              open qty matches the actual exchange wallet. Catches manual
              sells / transfers outside Haythix. */}
          {stats.drift && (
            <DriftBanner
              drift={stats.drift}
              asset={(bot?.symbol || '').split('/')[0] || 'asset'}
              onReconciled={() => onSync(bot.botId)}
            />
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Realized P&L" value={`$${formatNum(stats.realizedPnl, 2)}`} accent={stats.realizedPnl > 0 ? 'emerald' : undefined} />
            <Stat label="Round-trips" value={stats.completedRoundTrips} />
            <Stat label="Total fills" value={stats.totalFills} />
            <Stat label="Active orders" value={stats.activeOrders} />
          </div>

          {/* Open exposure — the "true at-risk" view. Grid bots only realize
              wins on close; unmatched buy lots are real capital still at risk
              from market drawdown. Always show this alongside the realized
              P&L so users don't mistake "100% win rate" for "no risk". */}
          {stats.openExposure && stats.openExposure.lotCount > 0 && (
            <div className="rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/5 to-dark-800/40 p-4">
              <div className="flex items-start gap-2 mb-3">
                <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-xs font-bold text-amber-300 uppercase tracking-wider">Unrealized open exposure</p>
                  <p className="text-[11px] text-amber-200/70 mt-0.5">
                    These are buy lots the bot has accumulated but hasn't sold yet. They count as profit-pending in the bot's view, but they're real capital exposed to drawdown if the price falls further.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat
                  label="Open lots"
                  value={stats.openExposure.lotCount}
                />
                <Stat
                  label="Cost basis"
                  value={`$${formatNum(stats.openExposure.openCostUsd, 2)}`}
                  subtext={`avg $${formatNum(stats.openExposure.avgOpenCostUsd, 2)}/unit`}
                />
                <Stat
                  label="Market value"
                  value={stats.openExposure.openMarketValueUsd != null ? `$${formatNum(stats.openExposure.openMarketValueUsd, 2)}` : '—'}
                  subtext={stats.openExposure.currentPriceUsd != null ? `at $${formatNum(stats.openExposure.currentPriceUsd, 2)}` : 'price unavailable'}
                />
                <Stat
                  label="Unrealized P&L"
                  value={stats.openExposure.unrealizedPnl != null
                    ? `${stats.openExposure.unrealizedPnl >= 0 ? '+' : ''}$${formatNum(stats.openExposure.unrealizedPnl, 2)}`
                    : '—'}
                  accent={stats.openExposure.unrealizedPnl != null
                    ? (stats.openExposure.unrealizedPnl >= 0 ? 'emerald' : 'red')
                    : undefined}
                  subtext={stats.openExposure.unrealizedPnlPct != null
                    ? `${stats.openExposure.unrealizedPnlPct >= 0 ? '+' : ''}${formatNum(stats.openExposure.unrealizedPnlPct, 2)}%`
                    : undefined}
                />
              </div>

              {/* Strong disclosure when win rate is structurally 100% — i.e.
                  bot has only closed wins so far AND has open lots that could
                  realize losses if forced closed today. */}
              {stats.completedRoundTrips > 0 &&
               stats.losses === 0 &&
               stats.openExposure.unrealizedPnl != null &&
               stats.openExposure.unrealizedPnl < 0 && (
                <div className="mt-3 bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                  <p className="text-[11px] text-red-200 leading-relaxed">
                    <strong className="text-red-300">Heads up:</strong> Your realized win rate is 100% ({stats.wins}W / 0L on closed round-trips), but you have {stats.openExposure.lotCount} open lots currently <strong>${formatNum(Math.abs(stats.openExposure.unrealizedPnl), 2)} below cost</strong>. If the price doesn't recover, those would become losses if you stop the bot. A grid bot's 100% win rate is structural — drawdown sits in open lots, not in the win/loss counter. Your real total P&L right now is <strong className={stats.realizedPnl + stats.openExposure.unrealizedPnl >= 0 ? 'text-emerald-300' : 'text-red-300'}>{stats.realizedPnl + stats.openExposure.unrealizedPnl >= 0 ? '+' : ''}${formatNum(stats.realizedPnl + stats.openExposure.unrealizedPnl, 2)}</strong> (realized + unrealized).
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Active orders ({activeOrders.length})</p>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {activeOrders.length === 0 ? (
            <p className="text-xs text-gray-600 italic">No open orders.</p>
          ) : activeOrders.map(o => (
            <div key={o.orderId} className="flex items-center justify-between text-xs bg-dark-700/30 rounded-lg p-2 font-mono">
              <span className={o.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}>{o.side.toUpperCase()}</span>
              <span className="text-gray-400">{formatNum(o.amount, 6)}</span>
              <span className="text-white">@${formatNum(o.price, 2)}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Recent fills ({fills.length})</p>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {fills.length === 0 ? (
            <p className="text-xs text-gray-600 italic">No fills yet. The first fill typically happens within the first 24h depending on volatility.</p>
          ) : fills.map((f, i) => (
            <div key={i} className="flex items-center justify-between text-xs bg-dark-700/30 rounded-lg p-2 font-mono">
              <span className={f.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}>{f.side.toUpperCase()}</span>
              <span className="text-gray-400">{formatNum(f.amount, 6)}</span>
              <span className="text-white">@${formatNum(f.price, 2)}</span>
              <span className="text-gray-600 text-[9px]">{new Date(f.recordedAt || f.timestamp).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
